import type { FrameName } from "@/game/entities/config";
import type {
	BackgroundImage,
	QuestNarrativeTypes,
} from "@/game/entities/config/quests/narrative/narrative_type.ts";
import type { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import type { QuestRewardTypes } from "@/game/entities/config/quests/quest_reward_types";
import type { QuestTypes } from "@/game/entities/config/quests/quest_types.ts";
import type { ResourceType } from "@/game/entities/config/resources";
import type { TileCoordinates } from "./tile";

export enum LevelAxis {
	ABOVE = "above", // axis y <= 512
	UNDER = "under", // axis y > 512
	LEFT = "left", // axis x <= 512
	RIGHT = "right", // axis x > 512
}

export type QuestId = number;

type Amount = number | (() => number);

// Base objective with common properties
export type QuestObjectiveBase = {
	action: QuestObjectiveActions;
	amount: Amount;
};

// Define each objective type with its specific properties
export type BuildBuildingObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.BUILD_BUILDING;
	type: FrameName | Set<FrameName>;
	built_building?: Amount;
};

export type ChangeTerrainObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.CHANGE_TERRAIN;
	type: FrameName | Set<FrameName>;
};

export type ClearTilesObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.CLEAR_TILES;
};

export type UseResourcesObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.USE_RESOURCES;
	type: ResourceType;
	pay?: number;
};

export type HaveTerrainObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.HAVE_TERRAIN;
	type: FrameName | Set<FrameName>;
};

export type UseSuperClickerObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.USE_SUPER_CLICKER;
};

export type UseClickMultiplierObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.USE_CLICK_MULTIPLIER;
};

export type DiscoverSpecificTileObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.DISCOVER_SPECIFIC_TILE;
	coordination: TileCoordinates;
};

export type ClearPlagueObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.CLEAR_PLAGUE;
	coordination: TileCoordinates;
	range: number;
};

export type HaveSettlementAxisObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.HAVE_SETTLEMENTS_AXIS;
	amount: Amount;
	level: number;
	axis: LevelAxis;
};

export type HaveSpecificSettlementOnLevelObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.HAVE_SPECIFIC_SETTLEMENT_ON_LEVEL;
	settlement_id: number;
};

export type HaveSettlementsOnLevelObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.HAVE_SETTLEMENTS_ON_LEVEL;
	level: number;
};

export type HaveResourceObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.HAVE_RESOURCE;
	type: ResourceType;
};

export type ConnectSpecificSettlementObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.CONNECT_SPECIFIC_SETTLEMENT;
	settlement_id: number;
};

export type ReachLevelObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.REACH_LEVEL;
};

export type FullSettlementsObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.HAVE_FULL_SETTLEMENTS;
	amount: Amount;
};

export type SalvageObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.SALVAGE;
	type: FrameName;
	amount: Amount;
};

export type CollectCoinsObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.COLLECT_COINS;
	amount: Amount;
};

export type DefeatObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.DEFEAT;
	type: FrameName | Set<FrameName>;
};

export type CompleteDailyQuestsObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.COMPLETE_DAILY_QUESTS;
	amount: Amount;
};

export type ClickOnSettlementObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.CLICK_ON_SETTLEMENT;
	amount: Amount;
};

export type WatchAdsObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.WATCH_ADS;
	amount: Amount;
};

export type SpendResourcesObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.SPEND_RESOURCES;
	type: ResourceType;
	amount: Amount;
};

export type IncreaseResourceIncomeObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.INCREASE_RESOURCE_INCOME;
	type: ResourceType;
	amount: Amount;
};

export type FinishQuestObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.FINISH_QUEST;
	quest_id: number;
};

export type PiggyBankProgressObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.PIGGY_BANK_PROGRESS;
	amount: Amount;
};

export type HaveStorageObjective = QuestObjectiveBase & {
	action: QuestObjectiveActions.HAVE_STORAGE;
	type: ResourceType;
	amount: Amount;
};

// Create a proper discriminated union
export type QuestObjective =
	| BuildBuildingObjective
	| ChangeTerrainObjective
	| ClearTilesObjective
	| UseResourcesObjective
	| HaveTerrainObjective
	| DiscoverSpecificTileObjective
	| HaveSpecificSettlementOnLevelObjective
	| HaveSettlementsOnLevelObjective
	| HaveResourceObjective
	| ReachLevelObjective
	| ConnectSpecificSettlementObjective
	| SalvageObjective
	| UseSuperClickerObjective
	| UseClickMultiplierObjective
	| HaveSettlementAxisObjective
	| FullSettlementsObjective
	| CollectCoinsObjective
	| DefeatObjective
	| CompleteDailyQuestsObjective
	| ClickOnSettlementObjective
	| WatchAdsObjective
	| SpendResourcesObjective
	| IncreaseResourceIncomeObjective
	| FinishQuestObjective
	| PiggyBankProgressObjective
	| ClearPlagueObjective
	| HaveStorageObjective;

export type QuestPrerequisite = QuestObjective;

export const EXPLORATION_SPEED_CONST = "ExplorationSpeed";
export const COINS_QUEST_REWARD_CONST = "COINS";

export type ResourceReward = {
	reward_resource: ResourceType;
	amount: Amount;
};

export type FrameReward = {
	reward_resource: FrameName;
	amount: Amount;
};

export type ExplorationReward = {
	reward_resource: typeof EXPLORATION_SPEED_CONST;
	amount: Amount;
};

// All possible reward types used in quests_template.ts
export type ResourceQuestReward = {
	type: QuestRewardTypes.RESOURCE;
	content: Array<ResourceReward>;
};

export type ResourceIncomeQuestReward = {
	type: QuestRewardTypes.RESOURCE_INCOME;
	content: Array<ResourceReward>;
};

export type ExplorationSpeedReward = {
	type: QuestRewardTypes.EXPLORATION_SPEED;
	content: Array<ExplorationReward>;
};

export type GetBuildingToStockQuestReward = {
	type: QuestRewardTypes.GET_BUILDING_TO_STOCK;
	content: Array<FrameReward>;
};

export type UnlockBuildingQuestReward = {
	type: QuestRewardTypes.UNLOCK_BUILDING;
	content: Array<FrameReward>;
};

export type UnlockMechanicQuestReward = {
	type: QuestRewardTypes.UNLOCK_MECHANIC;
	content: Array<FrameReward>;
};

export type UnlockUpgradeQuestReward = {
	type: QuestRewardTypes.UNLOCK_UPGRADE;
	content: Array<FrameReward>;
};

export type UnlockEventQuestReward = {
	type: QuestRewardTypes.UNLOCK_EVENT;
	content: Array<FrameReward>;
};

export type UnlockSettlementQuestReward = {
	type: QuestRewardTypes.UNLOCK_SETTLEMENT;
	content: Array<FrameReward>;
};

export type ChestsPlacementQuestReward = {
	type: QuestRewardTypes.CHESTS_PLACEMENT;
	content: Array<ResourceReward>;
};

export type PiggyBankReward = {
	type: QuestRewardTypes.PIGGY_BANK;
	content?: Array<never>;
};

// Union of all possible quest rewards
export type QuestReward =
	| ResourceQuestReward
	| ResourceIncomeQuestReward
	| GetBuildingToStockQuestReward
	| UnlockBuildingQuestReward
	| UnlockMechanicQuestReward
	| UnlockUpgradeQuestReward
	| UnlockEventQuestReward
	| UnlockSettlementQuestReward
	| ChestsPlacementQuestReward
	| ExplorationSpeedReward
	| PiggyBankReward;

export type QuestItem = {
	type: QuestTypes;
	title: string;
	flavor: string;
	hintText?: string;
	assignment: string;
	objective: QuestObjective;
	reward: QuestReward;
	badgeCoordinates?: TileCoordinates;
};

export type SideQuestsMarker = {
	markerCoordinates: TileCoordinates;
	prerequisites: QuestPrerequisite;
};

export type QuestTimestampEntry = {
	id: number;
	started_at?: string;
	completed_at?: string;
};

export interface QuestActive extends QuestItem {
	id: number;
	progress: number;
	payed?: number;
	built_building?: number;
	initial_state?: number; // Used for objectives that require tracking from a specific point (e.g., increase production by x)
	started_at?: string;
}

export enum NarrativeDirection {
	LEFT = "left",
	RIGHT = "right",
	CENTER = "center",
}

export type NarrativeItem = {
	quest_id: number;
	order_number: number;
	type: QuestNarrativeTypes;
	data: {
		image: string;
		backgroundImage?: BackgroundImage;
		direction?: NarrativeDirection;
		character_name: string;
		text: string;
	};
};

export enum QuestPopupType {
	NEW = "new",
	COMPLETED = "completed",
}

export type Lore = {
	id: number;
	img?: string;
	text: string;
};

export enum QuestPinStatus {
	PINNED = "quest_pinned",
	UNPINNED = "quest_unpinned",
	PINNED_CHANGED = "pinned_quest_changed",
}
